import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  DollarSign,
  Plus,
  Trash2,
  Zap,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { statsApi } from "@/lib/stats-api";

/**
 * Earnings section inside the LinkDetail sheet.
 *
 * Compact add-entry row:  [ amount ] [ note (optional) ] [ + ]
 *   - amount must parse as a positive number > 0 and <= 1,000,000
 *   - note is optional (short — sanity-capped to 200 chars server-side)
 *   - date defaults to server "today" (Dhaka)
 *
 * Entries list (newest first): amount · date · note · delete
 * Total displayed above the list.
 *
 * onChanged() bubbles to the parent so the dashboard can refetch the
 * link totals (for the card chip) after mutations.
 */
export default function EarningsSection({ link, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [error, setError] = useState(null);

  const linkId = link?.id;

  useEffect(() => {
    if (!linkId) return undefined;
    let cancelled = false;
    setLoading(true);
    statsApi
      .listEarnings(linkId)
      .then((rows) => {
        if (!cancelled) setEntries(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load earnings.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [linkId]);

  const totals = useMemo(() => {
    // Split into "this Dhaka month" vs "all-time" so the header can
    // show both. The Dhaka-month prefix is computed on the client (the
    // browser is time-zone-agnostic to Dhaka, but earning date strings
    // are already YYYY-MM-DD in Dhaka — so a prefix compare is safe).
    const now = new Date();
    const dhakaFmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Dhaka",
      year: "numeric",
      month: "2-digit",
    });
    // en-CA yields "YYYY-MM" cleanly. Fallback to raw month if not.
    const monthPrefix = dhakaFmt.format(now).slice(0, 7);
    let month = 0;
    let all = 0;
    for (const e of entries) {
      const amt = e.amount_usd || 0;
      all += amt;
      if ((e.date || "").startsWith(monthPrefix)) month += amt;
    }
    return { month, all };
  }, [entries]);

  const parsedAmount = useMemo(() => {
    if (!amount || amount.trim() === "") return null;
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0 || n > 1_000_000) return null;
    return n;
  }, [amount]);

  const canAdd = parsedAmount != null && !saving;

  const handleAdd = async () => {
    if (!canAdd) return;
    setSaving(true);
    try {
      const created = await statsApi.addEarning(linkId, {
        amount_usd: parsedAmount,
        note: note.trim() || undefined,
      });
      setEntries((rows) => [created, ...rows]);
      setAmount("");
      setNote("");
      onChanged && onChanged();
      toast.success(`Logged $${parsedAmount.toFixed(2)}`);
    } catch (e) {
      const msg =
        e?.response?.data?.detail?.[0]?.msg ||
        (typeof e?.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Couldn't save earning");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    // Trigger the confirm — never delete straight from a trash click.
    setConfirmTarget(entry);
  };

  const performDelete = async () => {
    const entry = confirmTarget;
    if (!entry) return;
    setDeletingId(entry.id);
    setConfirmTarget(null);
    try {
      await statsApi.deleteEarning(entry.id);
      setEntries((rows) => rows.filter((r) => r.id !== entry.id));
      onChanged && onChanged();
      toast.message("Earning removed", {
        description: `−$${Number(entry.amount_usd || 0).toFixed(2)}`,
      });
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section
      data-testid="earnings-section"
      className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-3 sm:px-4 sm:py-4"
    >
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted">
          Earnings · USD
        </h3>
        <div className="flex items-baseline gap-3">
          <div className="flex flex-col items-end leading-none">
            <span className="text-[9px] uppercase tracking-[0.18em] text-text-muted/70">
              This month
            </span>
            <span
              className="font-display text-lg font-bold text-accent"
              data-testid="earnings-total-month"
              data-total-usd={totals.month.toFixed(2)}
            >
              ${totals.month.toFixed(2)}
            </span>
          </div>
          <div className="flex flex-col items-end leading-none">
            <span className="text-[9px] uppercase tracking-[0.18em] text-text-muted/70">
              All-time
            </span>
            <span
              className="font-mono-num text-sm font-semibold text-text-main"
              data-testid="earnings-total-alltime"
              data-total-usd={totals.all.toFixed(2)}
            >
              ${totals.all.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Add-entry row */}
      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        data-testid="earnings-add-row"
      >
        <div className="relative flex-1 sm:max-w-[140px]">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted/70">
            <DollarSign className="h-3.5 w-3.5" />
          </span>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            inputMode="decimal"
            data-testid="earnings-amount-input"
            className="h-9 border-white/10 bg-black/30 pl-7 font-mono-num text-text-main focus-visible:border-accent focus-visible:ring-accent/50"
          />
        </div>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="note (optional)"
          maxLength={200}
          data-testid="earnings-note-input"
          className="h-9 min-w-0 flex-[2] border-white/10 bg-black/30 text-text-main focus-visible:border-accent focus-visible:ring-accent/50"
        />
        <Button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          data-testid="earnings-add-btn"
          className="h-9 gap-1 border border-accent/40 bg-accent/15 px-3 text-accent hover:bg-accent/25 disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="text-xs">Add</span>
        </Button>
      </div>

      {/* Entries */}
      {loading ? (
        <p className="rounded-lg border border-dashed border-white/10 py-4 text-center font-mono-safe text-[11px] text-text-muted">
          Loading…
        </p>
      ) : error ? (
        <p className="rounded-lg border border-red-500/20 bg-red-500/5 py-3 text-center font-mono-safe text-[11px] text-red-300">
          {error}
        </p>
      ) : entries.length === 0 ? (
        <p
          data-testid="earnings-empty"
          className="rounded-lg border border-dashed border-white/10 py-4 text-center font-mono-safe text-[11px] text-text-muted"
        >
          No earnings logged yet.
        </p>
      ) : (
        <ul
          className="flex flex-col gap-1.5"
          data-testid="earnings-list"
        >
          <AnimatePresence initial={false}>
            {entries.map((e) => {
              const isAuto = e.source === "auto";
              // Rate per auto click (safe against missing click_count).
              const perClick =
                isAuto && e.click_count > 0
                  ? e.amount_usd / e.click_count
                  : null;
              return (
              <motion.li
                key={e.id}
                data-testid="earnings-row"
                data-earning-id={e.id}
                data-earning-source={e.source || "manual"}
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.98 }}
                transition={{ type: "spring", stiffness: 380, damping: 24 }}
                className={
                  "flex items-center gap-2 rounded-lg border px-3 py-2 " +
                  (isAuto
                    ? "border-accent/20 bg-accent/[0.05]"
                    : "border-white/5 bg-white/[0.015]")
                }
              >
                <span
                  className="min-w-16 shrink-0 font-display text-base font-bold text-accent"
                  data-testid="earnings-row-amount"
                >
                  ${Number(e.amount_usd || 0).toFixed(2)}
                </span>
                <div className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span className="flex items-center gap-1.5 font-mono-num text-[11px] text-text-muted">
                    {isAuto && (
                      <span
                        data-testid="earnings-row-auto-pill"
                        title="Auto-added from clicks"
                        className="inline-flex items-center gap-0.5 rounded-full border border-accent/30 bg-accent/15 px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.15em] text-accent"
                      >
                        <Zap className="h-2.5 w-2.5" />
                        Auto
                      </span>
                    )}
                    <span>{e.date}</span>
                  </span>
                  {isAuto && perClick != null ? (
                    <span
                      className="truncate text-xs text-text-main/90"
                      data-testid="earnings-row-auto-formula"
                    >
                      {e.click_count} click{e.click_count === 1 ? "" : "s"} × $
                      {perClick.toFixed(2)}
                    </span>
                  ) : (
                    e.note && (
                      <span
                        className="truncate text-xs text-text-main"
                        title={e.note}
                      >
                        {e.note}
                      </span>
                    )
                  )}
                </div>
                {isAuto ? (
                  // Auto entries can't be manually deleted — they
                  // auto-adjust when the user hits undo on the card.
                  // A muted spacer keeps the row height consistent
                  // with manual rows so the list doesn't jitter.
                  <span
                    aria-hidden
                    data-testid="earnings-row-auto-lock"
                    title="Auto entries adjust via ± on the card"
                    className="flex h-8 w-8 shrink-0 items-center justify-center text-text-muted/30"
                  >
                    <Zap className="h-3.5 w-3.5" />
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleDelete(e)}
                    disabled={deletingId === e.id}
                    data-testid={`earnings-delete-${e.id}`}
                    aria-label="Delete earning"
                    className="focus-accent flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      )}

      {/* Confirm dialog — matches the link-delete pattern so destructive
          actions always require a second, explicit tap. */}
      <AlertDialog
        open={!!confirmTarget}
        onOpenChange={(v) => !v && setConfirmTarget(null)}
      >
        <AlertDialogContent
          data-testid="earnings-delete-confirm"
          className="border-white/10 bg-[#101012] text-text-main"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              Delete this earning?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-text-muted">
              <span
                className="font-display text-lg font-bold text-accent"
                data-testid="earnings-delete-confirm-amount"
              >
                ${Number(confirmTarget?.amount_usd || 0).toFixed(2)}
              </span>{" "}
              on {confirmTarget?.date}
              {confirmTarget?.note ? ` · ${confirmTarget.note}` : ""}.
              This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="earnings-delete-cancel"
              className="border-white/10 bg-transparent text-text-main hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="earnings-delete-confirm-btn"
              onClick={performDelete}
              className="bg-red-600/80 text-white hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
